import React from 'react';
import { isEmpty } from 'lodash';

import { SortableTable } from '~/components/core';
import EmptyState from '~/components/core/EmptyState';
import StatusChip from '~/components/core/StatusChip';
import Text from '~/components/core/TextComponents/Text';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import Switch from '~/components/ui/atoms/Switch';
import type { SlaRuleDTO } from '~/server_shared/openapi/data-contracts';
import { stringCmp, subOrgIdToNameDict } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

import { ENTITY_LEVEL_LABELS, TEXTS } from '../texts';
import type { SysconfigOrganization } from '../types';

interface SlaRulesTableProps {
  organization: SysconfigOrganization;
  rules: SlaRuleDTO[];
  isProcessing: boolean;
  canWrite: boolean;
  canDelete: boolean;
  emptyStateText: string;
  onEditRule: (rule: SlaRuleDTO) => void;
  onDeleteRule: (rule: SlaRuleDTO) => void | Promise<void>;
  onEnabledChanged: (rule: SlaRuleDTO) => void | Promise<void>;
}

interface Column {
  id: string;
  label: string;
  disableSort?: boolean;
  isHidden?: boolean;
  width?: string;
  specialCell?: (rule: SlaRuleDTO) => React.ReactNode;
  specialCmpFunc?: (a: SlaRuleDTO, b: SlaRuleDTO) => number;
}

const SlaRulesTable: React.FC<SlaRulesTableProps> = ({
  organization,
  rules,
  isProcessing,
  canWrite,
  canDelete,
  emptyStateText,
  onEditRule,
  onDeleteRule,
  onEnabledChanged,
}) => {
  const { lobConfigurationsDict } = useLobConfiguration();
  const subOrgIdToName = subOrgIdToNameDict(organization);

  // A rule that selects no sub-organization or LOB is unrestricted, so an empty scope reads "All".
  const subOrgLabel = (rule: SlaRuleDTO): string =>
    isEmpty(rule.sub_organization_ids)
      ? TEXTS.ALL
      : // A sub-organization the org no longer lists (removed since the rule was written) has no
        // name; fall back to its id so the cell still says the rule is scoped, rather than
        // dropping the entry and rendering blank — blank reads like "no scope" next to an "All"
        // row. Same fallback the LOB cell below makes for an unknown LOB key.
        (rule.sub_organization_ids ?? [])
          .map((id) => subOrgIdToName[id] ?? TEXTS.UNKNOWN_SUB_ORGANIZATION(id))
          .join(', ');
  const lobLabel = (rule: SlaRuleDTO): string =>
    isEmpty(rule.lobs)
      ? TEXTS.ALL
      : (rule.lobs ?? []).map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict) ?? lobKey).join(', ');
  // A configuration-repository import has no authoring user.
  const creatorLabel = (rule: SlaRuleDTO): string => rule.created_by_user_name ?? TEXTS.NO_CREATOR;

  // Sorts on the label the cell shows. The default comparator stringifies the raw value, which
  // would order a scope by its ids and an unrestricted or creatorless rule as the word "null".
  const byLabel = (label: (rule: SlaRuleDTO) => string) => (a: SlaRuleDTO, b: SlaRuleDTO) =>
    stringCmp(label(a), label(b));

  // Each option carries the verb its route requires: update is WRITE, delete is FULL.
  const actionOptions = (rule: SlaRuleDTO) => [
    ...(canWrite ? [{ onClick: () => onEditRule(rule), node: TEXTS.EDIT_ACTION, key: 'editRule' }] : []),
    ...(canDelete
      ? [
          {
            onClick: () => onDeleteRule(rule),
            withConfirmProps: {
              title: TEXTS.DELETE_CONFIRM_TITLE,
              contentText: TEXTS.DELETE_CONFIRM_TEXT(rule.display_name),
              primaryButtonName: TEXTS.DELETE_ACTION,
            },
            node: TEXTS.DELETE_ACTION,
            key: 'deleteRule',
          },
        ]
      : []),
  ];

  const columns: Column[] = [
    {
      id: 'display_name',
      label: TEXTS.COLUMN_NAME,
      specialCell: (rule) =>
        rule.error_state ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-8">
              {rule.display_name}
              <StatusChip status={StatusChip.STATUSES.ERROR} withIcon />
            </div>
            {/* Visible rather than in a tooltip: the flag is only actionable with its reason. */}
            <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.ERROR}>
              {rule.error_reason || rule.error_state}
            </Text>
          </div>
        ) : (
          rule.display_name
        ),
    },
    {
      id: 'sub_organization_ids',
      label: TEXTS.COLUMN_SUB_ORGANIZATIONS,
      isHidden: !organization.sub_organizations_enabled,
      specialCell: subOrgLabel,
      specialCmpFunc: byLabel(subOrgLabel),
    },
    { id: 'lobs', label: TEXTS.COLUMN_LOB, specialCell: lobLabel, specialCmpFunc: byLabel(lobLabel) },
    { id: 'entity_level', label: TEXTS.LEVEL_LABEL, specialCell: (rule) => ENTITY_LEVEL_LABELS[rule.entity_level] },
    {
      id: 'created_by_user_name',
      label: TEXTS.COLUMN_CREATED_BY,
      specialCell: creatorLabel,
      specialCmpFunc: byLabel(creatorLabel),
    },
    {
      id: 'enable',
      label: TEXTS.COLUMN_ENABLE,
      disableSort: true,
      specialCell: (rule) => (
        <Switch
          aria-label={TEXTS.ENABLE_SWITCH_ARIA_LABEL(rule.display_name)}
          onChange={() => onEnabledChanged(rule)}
          disabled={isProcessing || !canWrite}
          checked={rule.is_enabled}
        />
      ),
    },
    {
      id: 'actions',
      label: TEXTS.COLUMN_ACTIONS,
      width: '90px',
      disableSort: true,
      // A READ holder is offered neither Edit nor Delete, so for them the column would hold an empty menu.
      isHidden: !canWrite && !canDelete,
      specialCell: (rule) => (
        <div>
          <ThreeDotsMenu options={actionOptions(rule)} disabled={isProcessing} />
        </div>
      ),
    },
  ];

  return (
    <SortableTable columns={columns} rows={rules} emptyStateComponent={<EmptyState subtitle={emptyStateText} />} />
  );
};

export default SlaRulesTable;
